import * as React from "react";
import Input from "dt-design-system/es/input";
import Button from "dt-design-system/es/button";
import PaxPlanInput from "./pax-plan-input";
import useStay from "../hooks/use-stay";
import usePaxPlan from "../hooks/use-pax-plan";
import * as Dates from "../services/dates";
import message from "./message";
import css from "./filters.module.css";

const links = {
  fr: {
    lodgings:
      "https://reservation.saintfrancoislongchamp.com/hebergements/?hoteid=569",
  },
  en: {
    lodgings:
      "https://booking.saintfrancoislongchamp.com/accommodation/?hoteid=569",
  },
};

const Filters = ({ lang }) => {
  const ref = React.useRef(null);
  const [stay, setStay] = useStay();
  const [paxPlan, setPaxPlan] = usePaxPlan();
  const [touched, setTouched] = React.useState({ from: false, to: false });

  const updateTab = (newTab) => {
    setCurrentTab(newTab);
  };

  const updateStay = (bound) => (newValue) => {
    setStay({ ...stay, [bound]: newValue });
    setTouched({ ...touched, [bound]: true });

    if (bound === "from" && Boolean(newValue)) {
      focusStayToField();
    }
    if (bound === "to" && Boolean(newValue)) {
      focusPaxPlanField();
    }
  };

  const focusStayToField = () => {
    const stayToField = ref.current?.querySelector("[data-to]");
    if (stayToField) {
      stayToField.click();
      scrollToFilters();
    }
  };

  const focusPaxPlanField = () => {
    const paxPlanField = ref.current?.querySelector("[data-pax]");
    if (paxPlanField) {
      paxPlanField.click();
      scrollToFilters();
    }
  };

  const scrollToFilters = () => {
    ref.current.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "center",
    });
  };

  const fixBrokenStay = React.useCallback(() => {
    const fromDate = stay?.from && new Date(stay.from);
    const toDate = stay?.to && new Date(stay.to);

    if ((fromDate && !toDate) || (fromDate && toDate && fromDate > toDate)) {
      const newToDate = fromDate;
      newToDate.setDate(newToDate.getDate() + 1);
      setStay({ ...stay, to: Dates.format(newToDate) });
    }
    if (toDate && !fromDate) {
      const newFromDate = toDate;
      newFromDate.setDate(newFromDate.getDate() - 1);
      setStay({ ...stay, from: Dates.format(newFromDate) });
    }
  }, [setStay, stay]);

  const defaultStayToFieldMonth = stay?.from
    ? new Date(stay.from).getMonth() + 1
    : undefined;

  const defaultStayToFieldYear = stay?.from
    ? new Date(stay.from).getFullYear()
    : undefined;

  const submit = (e) => {
    e.preventDefault();
    fixBrokenStay();
    const endpoint = links[lang].lodgings;
    if (stay || paxPlan) {
      const paxPlanParam = paxPlan
        ? encodeURI(`&paxPlan=${JSON.stringify(paxPlan)}`)
        : "";
      const stayParams =
        stay?.from && stay?.to ? `&from=${stay.from}&to=${stay.to}` : "?";
      const endpointWithParams = endpoint.concat(stayParams, paxPlanParam);

      window.open(endpointWithParams, "_blank");
    } else {
      window.open(endpoint, "_blank");
    }
  };

  return (
    <form ref={ref} className={css.filters} onSubmit={submit}>
      <Input
        type="date"
        className={css.filter}
        label={message(lang, "arrival-label")}
        placeholder={message(lang, "arrival-placeholder")}
        value={stay?.from || ""}
        onValueChange={updateStay("from")}
        onMouseUp={scrollToFilters}
      />
      <Input
        type="date"
        className={css.filter}
        label={message(lang, "departure-label")}
        placeholder={message(lang, "departure-placeholder")}
        value={stay?.to || ""}
        hint={stay?.from}
        onValueChange={updateStay("to")}
        onMouseUp={scrollToFilters}
        min={stay?.from || undefined}
        defaultMonth={defaultStayToFieldMonth}
        defaultYear={defaultStayToFieldYear}
        data-to=""
      />
      <PaxPlanInput
        lang={lang}
        paxPlan={paxPlan}
        setPaxPlan={setPaxPlan}
        scrollToFilters={scrollToFilters}
      />
      <Button type="submit" className={css.submit}>
        {message(lang, "search")}
      </Button>
    </form>
  );
};

export default Filters;
